import { call, put, select, takeEvery } from 'redux-saga/effects';
import { selectMatchedStrategyCollection } from '../../../../Helpers/strategyCollection.helpers';
import {
  selectCurrentStepKey,
  selectUpstreamLocationStepKey,
} from '../../../../Helpers/clientApiJourneyPosition.helpers';
import { selectBookingLocation } from '../../../../Helpers/clientApiLocations.helpers';
import { fetchClientApiSlots } from '../GetClientApiSlots/fetchClientApiSlots';
import { isSlotAvailable } from '../../../../Components/ClientApi/slotDateTime.helpers';
import ActionTypes from '../../../../Data/ActionTypes';
import FetchStatus from '../../../../Data/FetchStatus';
import JourneyOutcome from '../../../../Data/JourneyOutcome';
import JourneyOutcomeDetail from '../../../../Data/JourneyOutcomeDetail';
import { incrementMetric } from '../../../../Services/Datadog';
import { getErrorTypeTag } from '../clientApiError.helpers';
import { getStepApi } from '../clientApiStep.helpers';
import { resolveDependencies } from '../clientApiDependencies.helpers';
import {
  setBookingReference,
  setBookingStatus,
  setStepResponse,
} from '../../slices/clientApiJourney';
import reportJourneyOutcomeOnce from '../reportJourneyOutcomeOnce';
import { captureWithSubmissionId } from './captureWithSubmissionId';
import { bookClientApiAppointment } from './bookClientApiAppointment';
import recordBookingEvent from './recordBookingEvent';

export const buildBookingDetails = ({
  referenceId,
  selectedLocation,
  slot,
}) => ({
  referenceId,
  ...(selectedLocation && {
    location: {
      id: selectedLocation.id,
      title: selectedLocation.title,
      address: selectedLocation.address,
    },
  }),
  ...(slot && { slot: { start: slot.start, end: slot.end } }),
});

// Swallows its own errors so a failed outcome send cannot break a confirmation.
function* reportBookingOutcome({ outcome, outcomeDetail, booking }) {
  const submissionId = yield select((state) => state.submission?.id);

  try {
    yield call(reportJourneyOutcomeOnce, {
      outcome,
      outcomeDetail,
      ...booking,
    });
  } catch (error) {
    incrementMetric({
      name: 'clientApiBookingOutcomeError',
      tags: { type: getErrorTypeTag(error), outcome },
    });
    captureWithSubmissionId({ error, submissionId });
  }
}

export function* submitClientApiBookingWorker(action) {
  const { slot } = action.payload;
  const submissionId = yield select((state) => state.submission?.id);
  const token = yield select((state) => state.submission?.token);
  const visitorId = yield select((state) => state.userIdentities?.visitorId);
  const bookingLocation = yield select(selectBookingLocation);

  const { location, slot: eventSlot } = buildBookingDetails({
    selectedLocation: bookingLocation,
    slot,
  });
  const eventBase = { slot: eventSlot, location, submissionId, visitorId };

  try {
    yield put(setBookingStatus({ bookingStatus: FetchStatus.IN_PROGRESS }));

    yield call(recordBookingEvent, {
      eventName: 'bookingAttempted',
      ...eventBase,
    });

    const matchedCollection = yield select(selectMatchedStrategyCollection);
    const stepKey = yield select(selectCurrentStepKey);
    const api = getStepApi({
      collection: matchedCollection,
      stepKey,
      operation: 'book',
    });

    if (!api?.url) {
      throw new Error(`No book operation on strategy step ${stepKey}`);
    }

    const stepStates = yield select(
      (state) => state.clientApiJourney.stepStates
    );
    const stepResponses = yield select(
      (state) => state.clientApiJourney.stepResponses
    );

    // The server books the clinic from the latest slots list it received, and
    // the user may have re-picked after a Review -> Back. So re-list the picked
    // clinic now, confirm the chosen slot is still offered, and only then book.
    // A failed list or a vanished slot throws into the catch, which ends the
    // journey — never book against a stale or missing slot.
    const listApi = getStepApi({
      collection: matchedCollection,
      stepKey,
      operation: 'list',
    });
    if (!listApi?.url) {
      throw new Error(`No list operation on strategy step ${stepKey}`);
    }
    const locationStepKey = yield select(selectUpstreamLocationStepKey);
    const listBody = resolveDependencies({
      dependencies: listApi?.dependencies,
      ownKey: stepKey,
      stepStates: locationStepKey
        ? { ...stepStates, [locationStepKey]: { location: bookingLocation } }
        : stepStates,
      stepResponses,
    });
    let slots;
    let listResponse;
    try {
      ({ slots, response: listResponse } = yield call(fetchClientApiSlots, {
        api: listApi,
        body: listBody,
        submissionId,
        token,
      }));
      const slotStillOffered = (slots ?? []).some(
        (offered) => offered.start === slot.start && isSlotAvailable(offered)
      );
      if (!slotStillOffered) {
        throw new Error('Picked slot is no longer offered');
      }
    } catch (error) {
      // Mark this as a pre-book re-list failure so the metric separates it from
      // an actual booking-request failure. Re-thrown so the journey still ends.
      error.stage = 'confirmRelist';
      throw error;
    }
    yield put(
      setStepResponse({ stepKey, operation: 'list', response: listResponse })
    );

    const body = resolveDependencies({
      dependencies: api.dependencies,
      ownKey: stepKey,
      stepStates,
      stepResponses,
    });

    const { referenceId, request, response } = yield call(
      bookClientApiAppointment,
      {
        api,
        body,
        submissionId,
        token,
      }
    );

    yield put(setBookingReference({ bookingReference: referenceId }));
    yield put(setStepResponse({ stepKey, operation: 'book', response }));
    yield put(setBookingStatus({ bookingStatus: FetchStatus.COMPLETED }));

    incrementMetric({ name: 'submitClientApiBookingSuccess' });

    yield call(recordBookingEvent, {
      eventName: 'bookingSucceeded',
      ...eventBase,
      request,
      outcome: JourneyOutcome.GOAL_ACHIEVED,
      outcomeDetail: JourneyOutcomeDetail.BOOKING_CONFIRMED,
      referenceId,
    });

    yield call(reportBookingOutcome, {
      outcome: JourneyOutcome.GOAL_ACHIEVED,
      outcomeDetail: JourneyOutcomeDetail.BOOKING_CONFIRMED,
      booking: buildBookingDetails({
        referenceId,
        selectedLocation: bookingLocation,
        slot,
      }),
    });
  } catch (error) {
    incrementMetric({
      name: 'submitClientApiBookingError',
      tags: {
        type: getErrorTypeTag(error),
        ...(error.stage && { stage: error.stage }),
      },
    });
    captureWithSubmissionId({ error, submissionId });
    yield put(setBookingStatus({ bookingStatus: FetchStatus.FAILED }));

    yield call(recordBookingEvent, {
      eventName: 'bookingFailed',
      ...eventBase,
      request: error.request,
      outcome: JourneyOutcome.GOAL_UNMET,
      outcomeDetail: JourneyOutcomeDetail.BOOKING_REQUEST_FAILED,
      error: {
        message: error.message,
        status: error.status,
        responseBody: error.responseBody,
      },
    });

    yield call(reportBookingOutcome, {
      outcome: JourneyOutcome.GOAL_UNMET,
      outcomeDetail: JourneyOutcomeDetail.BOOKING_REQUEST_FAILED,
    });
  }
}

export default function* submitClientApiBookingSaga() {
  yield takeEvery(
    ActionTypes.SUBMIT_CLIENT_API_BOOKING,
    submitClientApiBookingWorker
  );
}
